import CarouselError from "./CarouselError";
import MobileFacade from "@/facades/MobileFacade";

import {
  CarouselDirection,
  CarouselOrientation,
  CarouselTransition,
  SlidesPerPageInterface,
} from "@/components/Util/Layout/Carousel/CarouselInterface";

export default class CarouselEntity {
  private _dom!: HTMLElement;
  private _orientation!: CarouselOrientation;
  private _slidesPerPage!: SlidesPerPageInterface | number;
  private _transition!: CarouselTransition;

  private _currentSlide!: number;
  private _interval!: number;
  private _loop!: number;
  private _page!: number;

  private _autoHeight!: boolean;
  private _draggable!: boolean;
  private _hovered!: boolean;
  private _responsive!: boolean;
  private _showArrows!: boolean;
  private _showDots!: boolean;
  private _stopOnHover!: boolean;
  private _swipeable!: boolean;
  private _wheelable!: boolean;

  private _directionPageChange!: CarouselDirection;

  cx_isMobile = MobileFacade.isMobile();

  constructor(dom: HTMLElement) {
    this._hovered = false;
    this._dom = dom;
    this._page = 1;
    this.setChildrenAtts();
  }

  start(): void {
    this.startLoop();
  }

  stop(): void {
    this.stopLoop();
  }

  startLoop(): void {
    this.selectPage(this._currentSlide % this.getNumberOfPages());
    this.loop();
  }

  stopLoop(): void {
    if (typeof this._loop !== "undefined") clearInterval(this._loop);
  }

  loop(): void {
    this.stopLoop();
    this._loop = setInterval(() => {
      if (this._hovered && this._stopOnHover) return;

      this.incrementPage();
    }, this._interval);
  }

  /**
   * -----------------------------------------
   *          INITIAL CONFIGURATION
   * -----------------------------------------
   */

  setToLeft(): void {
    this.dom.classList.add("cx-carousel-to-left");
    this.dom.classList.remove("cx-carousel-to-right");
    this._directionPageChange = CarouselDirection.LEFT;
  }

  setToRight(): void {
    this.dom.classList.remove("cx-carousel-to-left");
    this.dom.classList.add("cx-carousel-to-right");
    this._directionPageChange = CarouselDirection.RIGHT;
  }

  setChildrenAtts(): void {
    this.dom.childNodes.forEach((child: ChildNode, index: number) => {
      const c = child as HTMLElement;
      c.setAttribute("data-slide-index", `${index}`);
    });
  }

  setActiveSlides(initialSlide: number): void {
    const slidesPerPage = this.cx_isMobile ? 1 : this.getNumberOfSlidesPerPage();
    let order = 1;

    if (this.getNumberOfPages() === 2 && this.dom.childNodes.length / 2 !== 0) {
      const oldInitialSlide: HTMLElement = this.dom.querySelector(
        '[data-slide-index-page="1"]'
      ) as HTMLElement;
      const newInitialIndex: number = parseInt(
        oldInitialSlide?.getAttribute("data-slide-index")?.toString() || "0"
      );

      this._currentSlide =
        this._directionPageChange === CarouselDirection.LEFT
          ? newInitialIndex - 1
          : newInitialIndex + 1;

      if (this._currentSlide < 0) this._currentSlide = this.dom.childNodes.length - 1;
      else if (this._currentSlide > this.dom.childNodes.length - 1) this._currentSlide = 0;

    } else this._currentSlide = initialSlide;

    this.setClearAllSlides();

    for (let i = this._currentSlide; i < this._currentSlide + slidesPerPage; i++) {
      try {
        const slide: HTMLElement = (this.dom.childNodes[i] as HTMLElement);
        
        slide.classList.add("cx-slide-active");
        slide.classList.remove("cx-slide-inactive");
        slide.setAttribute("data-slide-index-page", `${order}`);

        order++;
      } catch (e) {
        throw new CarouselError(
          "CarrouselEntity.ts > setActiveSlides: Call index " + i
        ).slideOutOfRange();
      }
    }
  }

  setClearAllSlides(): void {
    this.dom.childNodes.forEach((child: ChildNode) => {
      const c = child as HTMLElement;

      c.style.order = "";
      c.classList.remove("cx-slide-active");
      c.classList.add("cx-slide-inactive");
      c.setAttribute("data-slide-index-page", "");
    });
  }

  getNumberOfSlidesPerPage(): number {
    if (typeof this._slidesPerPage === "number") return this._slidesPerPage;
    if (window.innerWidth < 860) return 1;
    const perPage = Object.values(this._slidesPerPage).find((p) => p.size > window.innerWidth);

    if (perPage) return perPage.slidesPerPage;
    return this._slidesPerPage.xl?.slidesPerPage || 0;
  }

  /**
   * -----------------------------------------
   *                   PAGES
   * -----------------------------------------
   */

  getNumberOfPages(): number {
    return Math.ceil(this.dom.childNodes.length / this.getNumberOfSlidesPerPage());
  }

  incrementPage(): void {
    this._page = this._page + 1 > this.getNumberOfPages() ? 1 : this._page + 1;

    this._currentSlide = this._page * this.getNumberOfSlidesPerPage() - this.getNumberOfSlidesPerPage();

    this.setToRight();
    this.setActiveSlides(this._currentSlide);
  }

  decrementPage(): void {
    this._page = this._page - 1 < 1 ? this.getNumberOfPages() : this._page - 1;

    this._currentSlide = this._page * this.getNumberOfSlidesPerPage() - this.getNumberOfSlidesPerPage();

    this.setToLeft();
    this.setActiveSlides(this._currentSlide);
  }

  selectPage(page: number): void {
    if (page < 1) this._page = 1;
    else if (page > this.getNumberOfPages()) this._page = this.getNumberOfPages();
    else this._page = page;

    const previewCurrent = this._currentSlide;
    this._currentSlide =
      this._page * this.getNumberOfSlidesPerPage() - this.getNumberOfSlidesPerPage();

    if (previewCurrent < this._currentSlide) this.setToLeft();
    else this.setToRight();

    this.setActiveSlides(this._currentSlide);
  }

  /**
   * -----------------------------------------
   *                  EVENTS
   * -----------------------------------------
   */

  onHover(): void {
    this._hovered = true;
  }

  onLeave(): void {
    this._hovered = false;
  }

  onDrag(event: DragEvent): void {
    event;
  }

  onDragEnter(event: DragEvent): void {
    event;
  }

  onDragOver(event: DragEvent): void {
    event;
  }

  onDragLeave(event: DragEvent): void {
    event;
  }

  onDragStart(event: DragEvent): void {
    event;
  }

  onDragEnd(event: any): void {
    if (event.direction === CarouselDirection.LEFT) this.incrementPage();
    else if (event.direction === CarouselDirection.RIGHT) this.decrementPage();
  }

  onResize(): void {
    this.setActiveSlides(this._currentSlide);
  }

  /**
   * -----------------------------------------
   * GETTERS AND SETTERS
   * -----------------------------------------
   */

  public set slidesPerPage(slidesPerPage: SlidesPerPageInterface | number) {
    if (
      typeof slidesPerPage === "number" &&
      (!Number.isInteger(slidesPerPage) || slidesPerPage < 1)
    ) {
      throw new CarouselError("CarouselEntity.ts > slidesPerPage").slideOutOfRange();
    }

    this._slidesPerPage = slidesPerPage;
  }

  public get slidesPerPage(): SlidesPerPageInterface | number {
    return this._slidesPerPage;
  }

  public get page(): number {
    return this._page;
  }

  public set responsive(responsive: boolean) {
    this._responsive = responsive;
  }

  public get responsive(): boolean {
    return this._responsive;
  }

  public set orientation(orientation: CarouselOrientation) {
    this._orientation = orientation;
  }

  public get orientation(): CarouselOrientation {
    return this._orientation;
  }

  public set transition(transition: CarouselTransition) {
    this._transition = transition;
  }

  public get transition(): CarouselTransition {
    return this._transition;
  }

  public set interval(interval: number) {
    if (!Number.isInteger(interval) || interval < 0) {
      throw new CarouselError("CarouselEntity.ts > interval").intervalOutOfRange();
    }

    this._interval = interval;
  }

  public get interval(): number {
    return this._interval;
  }

  public set draggable(draggable: boolean) {
    this._draggable = draggable;
  }

  public get draggable(): boolean {
    return this._draggable;
  }

  public set showDots(showDots: boolean) {
    this._showDots = showDots;
  }

  public get showDots(): boolean {
    return this._showDots;
  }

  public set showArrows(showArrows: boolean) {
    this._showArrows = showArrows;
  }

  public get showArrows(): boolean {
    return this._showArrows;
  }

  public set currentSlide(currentSlide: number) {
    if (!Number.isInteger(currentSlide) || currentSlide < 0) {
      throw new CarouselError("CarouselEntity.ts > currentSlide").currentSlideOutOfRange();
    }

    this._currentSlide = currentSlide;
  }

  public get currentSlide(): number {
    return this._currentSlide;
  }

  public set stopOnHover(stopOnHover: boolean) {
    this._stopOnHover = stopOnHover;
  }

  public get stopOnHover(): boolean {
    return this._stopOnHover;
  }

  public set wheelable(wheelable: boolean) {
    this._wheelable = wheelable;
  }

  public get wheelable(): boolean {
    return this._wheelable;
  }

  public get dom(): HTMLElement {
    return this._dom;
  }

  public set swipeable(swipeable: boolean) {
    this._swipeable = swipeable;
  }

  public get swipeable(): boolean {
    return this._swipeable;
  }

  public set autoHeight(autoHeight: boolean) {
    this._autoHeight = autoHeight;
  }

  public get autoHeight(): boolean {
    return this._autoHeight;
  }
}
